import { api } from "@/lib/api/client";
import type {
  ArtistApplication,
  DetailResponse,
  JobApplication,
  ListFilters,
  PaginatedResponse,
  ProducerApplication,
  ServiceProviderApplication,
} from "./types";

function buildParams(filters: ListFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters.status) out.status = filters.status;
  if (filters.country) out.country = filters.country;
  if (filters.q) out.q = filters.q;
  if (filters.page) out.page = String(filters.page);
  if (filters.pageSize) out.pageSize = String(filters.pageSize);
  return out;
}

// ── Producers ────────────────────────────────────────────────────────
export const producersApi = {
  list: async (filters: ListFilters): Promise<PaginatedResponse<ProducerApplication>> => {
    const { data } = await api.get("/admin/producer-applications", {
      params: buildParams(filters),
    });
    return data;
  },
  detail: async (id: number): Promise<ProducerApplication> => {
    const { data } = await api.get<DetailResponse<ProducerApplication>>(
      `/admin/producer-applications/${id}`,
    );
    return data.application;
  },
  update: async (
    id: number,
    body: { status?: string; internalNotes?: string | null },
  ): Promise<ProducerApplication> => {
    const { data } = await api.patch<DetailResponse<ProducerApplication>>(
      `/admin/producer-applications/${id}`,
      body,
    );
    return data.application;
  },
  approve: async (id: number): Promise<ProducerApplication> => {
    const { data } = await api.post<DetailResponse<ProducerApplication>>(
      `/admin/producer-applications/${id}/approve`,
    );
    return data.application;
  },
  reject: async (id: number, reason?: string): Promise<ProducerApplication> => {
    const { data } = await api.post<DetailResponse<ProducerApplication>>(
      `/admin/producer-applications/${id}/reject`,
      { reason: reason || null },
    );
    return data.application;
  },
  exportCsv: (filters: ListFilters): string => {
    const search = new URLSearchParams(buildParams(filters)).toString();
    return `/admin/producer-applications/export${search ? `?${search}` : ""}`;
  },
};

// ── Artists ──────────────────────────────────────────────────────────
export const artistsApi = {
  list: async (filters: ListFilters): Promise<PaginatedResponse<ArtistApplication>> => {
    const { data } = await api.get("/admin/artist-applications", {
      params: buildParams(filters),
    });
    return data;
  },
  detail: async (id: number): Promise<ArtistApplication> => {
    const { data } = await api.get<DetailResponse<ArtistApplication>>(
      `/admin/artist-applications/${id}`,
    );
    return data.application;
  },
  update: async (
    id: number,
    body: { status?: string; internalNotes?: string | null },
  ): Promise<ArtistApplication> => {
    const { data } = await api.patch<DetailResponse<ArtistApplication>>(
      `/admin/artist-applications/${id}`,
      body,
    );
    return data.application;
  },
  approve: async (id: number): Promise<ArtistApplication> => {
    const { data } = await api.post<DetailResponse<ArtistApplication>>(
      `/admin/artist-applications/${id}/approve`,
    );
    return data.application;
  },
  reject: async (id: number, reason?: string): Promise<ArtistApplication> => {
    const { data } = await api.post<DetailResponse<ArtistApplication>>(
      `/admin/artist-applications/${id}/reject`,
      { reason: reason || null },
    );
    return data.application;
  },
  exportCsv: (filters: ListFilters): string => {
    const search = new URLSearchParams(buildParams(filters)).toString();
    return `/admin/artist-applications/export${search ? `?${search}` : ""}`;
  },
};

// ── Service Providers ────────────────────────────────────────────────
// OJO: a diferencia de producers/artists/jobs (envelope { applications } /
// { application }), el backend de service-providers responde { providers } /
// { provider }. Normalizamos acá al envelope común para que las pantallas
// compartidas no cambien.
interface ProviderListResponse {
  providers: ServiceProviderApplication[];
  page: number;
  pageSize: number;
  total: number;
}

interface ProviderDetailResponse {
  provider: ServiceProviderApplication;
}

export const providersApi = {
  list: async (filters: ListFilters): Promise<PaginatedResponse<ServiceProviderApplication>> => {
    const { data } = await api.get<ProviderListResponse>("/admin/service-providers", {
      params: buildParams(filters),
    });
    return {
      applications: data.providers ?? [],
      page: data.page,
      pageSize: data.pageSize,
      total: data.total,
    };
  },
  detail: async (id: number): Promise<ServiceProviderApplication> => {
    const { data } = await api.get<ProviderDetailResponse>(
      `/admin/service-providers/${id}`,
    );
    return data.provider;
  },
  update: async (
    id: number,
    body: { status?: string; internalNotes?: string | null },
  ): Promise<ServiceProviderApplication> => {
    const { data } = await api.patch<ProviderDetailResponse>(
      `/admin/service-providers/${id}`,
      body,
    );
    return data.provider;
  },
  verify: async (id: number): Promise<ServiceProviderApplication> => {
    const { data } = await api.post<ProviderDetailResponse>(
      `/admin/service-providers/${id}/verify`,
    );
    return data.provider;
  },
  reject: async (id: number, reason?: string): Promise<ServiceProviderApplication> => {
    const { data } = await api.post<ProviderDetailResponse>(
      `/admin/service-providers/${id}/reject`,
      { reason: reason || null },
    );
    return data.provider;
  },
  exportCsv: (filters: ListFilters): string => {
    const search = new URLSearchParams(buildParams(filters)).toString();
    return `/admin/service-providers/export${search ? `?${search}` : ""}`;
  },
};

// ── Jobs ─────────────────────────────────────────────────────────────
// Mismo shape paginado que las demás. Soporta filtros status / q / page.
// No tiene country, ni approve/reject, ni export.
export const jobsApi = {
  list: async (filters: ListFilters): Promise<PaginatedResponse<JobApplication>> => {
    const { data } = await api.get<PaginatedResponse<JobApplication>>(
      "/admin/job-applications",
      { params: buildParams(filters) },
    );
    return data;
  },
  detail: async (id: number): Promise<JobApplication> => {
    const { data } = await api.get<DetailResponse<JobApplication>>(
      `/admin/job-applications/${id}`,
    );
    return data.application;
  },
  update: async (
    id: number,
    body: { status?: string; internalNotes?: string | null },
  ): Promise<JobApplication> => {
    const { data } = await api.patch<DetailResponse<JobApplication>>(
      `/admin/job-applications/${id}`,
      body,
    );
    return data.application;
  },
};
